import React, { useCallback } from "react";
import { StyleSheet, View } from "react-native";
import { ListItem } from "@rneui/themed";
import { useDispatch } from "react-redux";
import _ from "lodash";
import variables from "src/common/assets/variables";
import { getImageUrl } from "libs/utils/image";
import { PopularItemResponse } from "src/common/types/response/storeItems";
import { CartItemProps } from "src/common/types/cart";
import CounterButton from "./counterButtons";
import Text from "./text";
import useMaster from "../libs/hooks/useMaster";
import GreyScaleImage from "./greyscaleImage";
import { ImageMapper } from "../assets/images";
import { addItem, removeItem } from "../toolkit/features/cart";

function ifExistInCart(items, item: PopularItemResponse) {
  let inCart = false;
  let cartItem: CartItemProps;

  items.every((it) => {
    if (it._id === item._id) {
      inCart = true;
      cartItem = it;
      return false;
    } else {
      inCart = false;
    }
    return true;
  });

  return { inCart, cartItem };
}

const PopularItem = ({
  item,
  cartItems,
  enabled = true,
  squared = false,
  style = {},
  squaredImageStyle = {},
  onDec,
  onInc
}: {
  item: PopularItemResponse & { header?: boolean; footer?: boolean };
  cartItems: CartItemProps[];
  enabled?: boolean;
  squared?: boolean;
  style?: object;
  onDec?: (item: PopularItemResponse)=>void
  onInc?: (item: PopularItemResponse)=>void
  squaredImageStyle?: object;
}) => {
  const master = useMaster();
  const dispatch = useDispatch();
  const available = item.is_available && item.stock > 0 && enabled;
  const imgSrc = item?.images && getImageUrl(item?.images[0]);
  const { inCart, cartItem } = cartItems
    ? ifExistInCart(cartItems, item)
    : { inCart: false, cartItem: [] };

  const addToCart = useCallback(
    (item: PopularItemResponse) => {
      if (available) {
        dispatch(addItem(item));
      }
    },
    [dispatch]
  );

  const removeFromCart = useCallback(
    (item: PopularItemResponse) => {
      dispatch(removeItem(item));
    },
    [dispatch]
  );

  if (item.footer) {
    return null;
  }

  if (item.header) {
    return (
      <View style={styles.header}>
        <Text style={styles.headerName}>{item.name}</Text>
      </View>
    );
  }

  let name = item.name;
  const unit =
    master && master?.data?.payload?.units?.find((it) => it.id === item.unit);
  name = item && `${item.name?.trim()} (${item.measure}${unit?.abbr || ""})`;

  const price = item.price.toFixed(2);

  if (squared) {
    return (
      <View style={[styles.parentSquaredStyle, style]}>
        <View style={[styles.squaredImageParent, squaredImageStyle]}>
          <GreyScaleImage
            source={{ uri: imgSrc }}
            style={styles.squaredImage}
            greyscale={!available}
            resizeMode="cover"
            placeholderStyle={styles.placeholderImage}
            placeHolderImage={ImageMapper.NoImage}
          />
        </View>
        <View style={{ alignSelf: "flex-start", maxWidth: "100%", height: 80 }}>
          {name && (
            <ListItem.Title
              style={[styles.name, styles.squaredName]}
              numberOfLines={3}
            >
              {name}
            </ListItem.Title>
          )}
          {/* {unit && item.measure && (
            <ListItem.Title style={[styles.name, styles.squaredName]}>
              {item.measure} {unit?.abbr || ""}
            </ListItem.Title>
          )} */}
          {
            <View style={styles.priceSquared}>
              {!!item.price_mrp && item.price_mrp !== parseInt(price) && (
                <ListItem.Subtitle
                  style={styles.mrp}
                >{`MRP ${item.price_mrp}`}</ListItem.Subtitle>
              )}
              <ListItem.Subtitle
                style={[styles.price, styles.pricesSquared]}
              >{`INR ${price}`}</ListItem.Subtitle>
            </View>
          }
        </View>
        <CounterButton
          item={cartItem}
          rootItem={item}
          onDecrement={onDec || removeFromCart}
          onIncrement={onInc || addToCart}
          stock={item?.stock}
          inCart={inCart}
          solidAdd
          available={available}
          squared
        />
      </View>
    );
  }

  return (
    <ListItem containerStyle={[styles.root, style]}>
      <GreyScaleImage
        source={{ uri: imgSrc }}
        style={styles.image}
        greyscale={!available}
        placeHolderImage={ImageMapper.NoImage}
      />
      <ListItem.Content>
        {name && (
          <ListItem.Title style={styles.name} numberOfLines={2}>
            {name}
          </ListItem.Title>
        )}
        {!!item.price_mrp && item.price_mrp !== parseInt(price) && (
          <ListItem.Subtitle
            style={styles.mrp}
          >{`MRP ${item.price_mrp}`}</ListItem.Subtitle>
        )}
        <ListItem.Subtitle
          style={styles.price}
        >{`INR ${price}`}</ListItem.Subtitle>
      </ListItem.Content>
      <CounterButton
        item={cartItem}
        rootItem={item}
        onDecrement={onDec || removeFromCart}
        onIncrement={onInc || addToCart}
        stock={item?.stock}
        inCart={inCart}
        available={available}
        squared={false}
      />
    </ListItem>
  );
};

const styles = StyleSheet.create({
  root: {
    paddingLeft: 0,
    paddingTop: 10,
    paddingBottom: 5,
    paddingRight: 0,
    height: 77,
  },
  priceSquared: {
    flex: 1,
    justifyContent: "flex-end",
  },
  parentSquaredStyle: {
    padding: 10,
    alignItems: "center",
    flex: 1,
    paddingBottom: 12,
  },
  squaredStyle: {
    flexDirection: "column",
    marginRight: 10,
  },
  squaredImageParent: {
    padding: 10,
    borderWidth: 1,
    borderRadius: 8,
    borderColor: variables.$disabledText,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    height: 155,
    marginBottom: 5,
  },
  placeholderImage: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  squaredImage: {
    height: "100%",
    width: "100%",
    aspectRatio: 1,
  },
  image: {
    height: 60,
    width: 60,
    borderRadius: 15,
    overflow: "hidden",
  },
  price: {
    color: "#F9A26C",
    fontSize: 12,
    lineHeight: 16,
    fontFamily: "MontserratBold",
  },
  pricesSquared: {
    fontSize: 14,
  },
  mrp: {
    color: variables.$textDark,
    fontSize: 12,
    lineHeight: 16,
    textDecorationLine: "line-through",
  },
  name: {
    color: "#636E72",
    fontSize: 14,
    lineHeight: 16,
  },
  squaredName: {
    fontFamily: "MontserratBold",
    fontSize: 12,
  },
  header: {
    borderBottomColor: "#F4F4F4",
    borderBottomWidth: 1,
    paddingBottom: 8,
    marginTop: 8,
  },
  headerName: {
    color: "#636E72",
    fontSize: 14,
    fontFamily: "MontserratBold",
  },
});

export default React.memo(PopularItem, (prevProps, nextProps) => {
  const isPropsEquals = _.isEqual(prevProps, nextProps);
  return isPropsEquals;
});
